import axios from 'axios';
import { useAccessStore } from '@/store/accessStore';
import { useSubscriptionStore } from '@/store/subscriptionStore'; // fixed path casing

// Helper to extract projectId from current URL path
function getProjectIdFromUrl(): string {
  const path = window.location.pathname;
  // Match patterns like /p1/project/ABC123 or /project/ABC123
  const match = path.match(
    /\/(?:p1\/)?(?:project|dashboard|organization|templates)\/([^/]+)/
  );
  return match ? match[1] : '';
}

// Check if we're on a page that handles its own errors (shouldn't show global modal)
function isOnSelfHandledErrorPage(): boolean {
  const path = window.location.pathname;
  // approve-access page has its own error UI
  return path.includes('/approve-access');
}

export const handleAxiosError = (error: unknown): void => {
  // Skip global error handling for pages that handle their own errors
  if (isOnSelfHandledErrorPage()) {
    return;
  }

  if (axios.isAxiosError(error)) {
    if (error.response) {
      const accessStore = useAccessStore();
      const responseData = error.response.data;
      const projectId = getProjectIdFromUrl();

      // New check for Missing Authorization error using access store
      // New check for "Agent does not have access to this project"
      if (
        (typeof responseData === 'string' &&
          responseData.includes(
            'Agent does not have access to this project'
          )) ||
        (typeof responseData === 'object' &&
          responseData?.error === 'Agent does not have access to this project')
      ) {
        accessStore.setNoAccess(
          projectId,
          'Access denied: You do not have access to this project. Please ask the owner to add you or set the project to public.'
        );
      } else if (
        (typeof responseData === 'string' &&
          responseData.includes('Missing Authorization')) ||
        (typeof responseData === 'object' &&
          responseData?.error === 'Missing Authorization')
      ) {
        accessStore.setNoAccess(
          projectId,
          'Access denied: You must sign in to see if you have access.'
        );
      }
      // New check for subscription insufficiency (string)
      else if (
        typeof responseData === 'string' &&
        responseData
          .toLowerCase()
          .includes('tenant subscription not sufficient:')
      ) {
        const lower = responseData.toLowerCase();
        const match = lower.match(
          /tenant subscription not sufficient:\(type=(templates|members|versions)\)/
        );
        if (match) {
          const type = match[1] as 'templates' | 'members' | 'versions';
          const subscriptionStore = useSubscriptionStore();
          subscriptionStore.openSubscriptionModal(type);
        }
      }
      // New block: handle object response with errors array for subscription insufficiency
      else if (
        typeof responseData === 'object' &&
        responseData !== null &&
        'errors' in responseData &&
        Array.isArray((responseData as { errors?: unknown }).errors)
      ) {
        const errorsArray = (responseData as { errors: unknown[] }).errors;
        for (const item of errorsArray) {
          if (
            typeof item === 'object' &&
            item !== null &&
            'message' in item &&
            typeof (item as { message?: unknown }).message === 'string'
          ) {
            const msg = (item as { message: string }).message;
            const lowerMsg = msg.toLowerCase();
            if (lowerMsg.includes('tenant subscription not sufficient')) {
              const match = lowerMsg.match(
                /tenant subscription not sufficient:\(type=(templates|members|versions)\)/
              );
              if (match) {
                const type = match[1] as 'templates' | 'members' | 'versions';
                const subscriptionStore = useSubscriptionStore();
                subscriptionStore.openSubscriptionModal(type);
                break;
              }
            }
          }
        }
      }
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
      console.error('Response headers:', error.response.headers);
    } else if (error.request) {
      console.error('Request made but no response received:', error.request);
    } else {
      console.error('Error', error.message);
    }
  } else {
    console.error('Unexpected error:', error);
  }
};
